/**
 * Component: Cart table
 * ------------------------------------------------------------------------------
 * An event based Ajax cart table.
 *
 * @namespace cartTable
 */
import * as cart from '@shopify/theme-cart';
import { formatMoney } from '@shopify/theme-currency';
import { getSizedImageUrl } from '@shopify/theme-images';
import axios from 'axios';

import cssClasses from '~/helpers/cssClasses';
import { on, extendDefaults, decodeHTMLEntities, handleize } from '~/helpers/utils';
import quantitySelectorTemplate from '~/components/templates/quantity-selector';

import QuantitySelector from '~/components/quantity-selector';

/**
 * DOM selectors.
 */
const selectors = {
  ajaxCart: 'js-ajax-cart',
  lineItem: '[js-cart-table="lineItem"]',
  productJson: '[js-product-json]',
  lineItemPrice: '[js-cart-table="lineItemPrice"]',
  quantitySelector: '[js-cart-table="quantitySelector"]',
  subtotal: '[js-cart-table="subtotal"]',
  total: '[js-cart-table="total"]',
  totalDiscountContainer: '[js-cart-table="totalDiscountContainer"]',
  totalDiscountPrice: '[js-cart-table="totalDiscountPrice"]',
  footnote: '[js-cart-table="footnote"]',
  footnoteContent: '[js-cart-table="footnoteContent"]',
  tooltipClose: '[js-cart-table="tooltipClose"]',
  tooltip: '[js-cart-table="tooltip"]',
  productsLinks: '[js-ajax-cart="productLink"]',
  keepShopping: '.template-cart__shop',
  submit: '[js-cart-table="submit"]',
  termsEngraving: '[js-cart-table="termsEngraving"]',
  termsContainer: '[js-cart-table="termsContainer"]',
};

/**
 * Create new cart table object.
 * @param {String} selector - DOM selector.
 */
export default (selector, config) => {

  /**
   * Instance default settings.
   */
  const defaults = {
    hideAddOns: true,
  };

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selector),
    subtotal: document.querySelectorAll(selectors.subtotal),
    total: document.querySelector(selectors.total),
    totalDiscountContainer: document.querySelector(selectors.totalDiscountContainer),
    totalDiscountPrice: document.querySelector(selectors.totalDiscountPrice),
    footnote: document.querySelectorAll(selectors.footnote),
    footnoteContent: document.querySelectorAll(selectors.footnoteContent),
    tooltipClose: document.querySelector(selectors.tooltipClose),
    tooltip: document.querySelector(selectors.tooltip),
    keepShopping: document.querySelector(selectors.keepShopping),
    submit: document.querySelector(selectors.submit),
    termsEngraving: document.querySelector(selectors.termsEngraving),
    termsContainer: document.querySelector(selectors.termsContainer),
  };

  /**
   * Instance settings.
   */
  const settings = extendDefaults(defaults, config);

  /**
   * Instance globals.
   */
  let timer = {};
  let handlingQuantity = false;
  let cancelToken;

  /**
   * Initialise component.
   */
  function init() {
    requestCart();
    setEventBusListeners();
    setEventListeners(nodeSelectors.container);
    disableAddToCart();
  }



  /**
   * Set EventBus listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('AjaxCart:updated', (response) => render(response));
    Frame.EventBus.listen('AjaxCart:addOnItemRemoved', async () => {
      await axios.get(`/cart.js?no-cache=${new Date().getTime()}`)
        .then((response) => {
          console.log('RESPONSE DATA', response.data);
          Frame.EventBus.emit('AjaxCart:updated', response.data);
        })
        .catch((error) => {
          throw new Error(error);
        });
    });
    Frame.EventBus.listen('AjaxCart:removingItem', (item) => setLoadingState(item));
    if (nodeSelectors.termsContainer !== null) {
      on('change', nodeSelectors.termsContainer, () => toggleAddToCart())
    }

    on('click', nodeSelectors.keepShopping, () => {
      window.dataLayer.push({
        event: 'GAevent',
        eventID: '19',
        eventCategory: 'cart',
        eventAction: 'cart_interaction',
        eventLabel: 'continue_shopping',
      });
    });

    on('click', nodeSelectors.submit, () => {
      window.dataLayer.push({
        event: 'GAevent',
        eventID: '19',
        eventCategory: 'cart',
        eventAction: 'cart_interaction',
        eventLabel: 'go_to_checkout',
      });
    });
  }

  /**
   * Requests cart.js data for rendering.
   */
  function requestCart() {
    Frame.EventBus.emit('CartTable:updating');

    axios.get(`/cart.js?no-cache=${new Date().getTime()}`)
      .then((response) => {
        render(response.data);
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /**
   * Set listeners for datalayer events.
   */
  function dataLayerLinks() {
    on('click', nodeSelectors.container, (event) => {
      if (!event.target.closest(selectors.productsLinks)) {
        return;
      }

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '19',
        eventCategory: 'cart',
        eventAction: 'cart_interaction',
        eventLabel: 'go_to_product',
      });
    });
  }

  function updateCompareAtPrices({ lineItem, compareAtPrice }) {
    const compareAtPlaceholder = lineItem.querySelector('[data-compare-at-price]');
    if (compareAtPlaceholder) {
      compareAtPlaceholder.setAttribute('data-compare-at-price', compareAtPrice);
      compareAtPlaceholder.style = 'display: block;';
      compareAtPlaceholder.innerHTML = compareAtPrice;
    }
  }

  function updateCompareAtPricesCache({ key, compareAtPrice }) {
    const updatableCacheEntry = window.AlessiCartPrices.find(priceObj => priceObj.key == key);

    if (!updatableCacheEntry) {
      window.AlessiCartPrices.push({
        "key": key,
        "compare_at_price": compareAtPrice,
      });
    }
  }

  /**
   * Render line items compare at prices.
   * @param {Object} data - Cart.js data.
   */
  async function loadCompareAtPrices(data) {
    if (window.AlessiCartPrices) {
      for (let i = 0; i < data.items.length; ++i) {
        const currentItem = data.items[i];

        // Using this to update cache at the end
        let currentItemComparePrice = '';

        const currentItemPrices = window.AlessiCartPrices.find(priceObj => priceObj.key == currentItem.key);

        if (currentItemPrices) {
          const lineItem = document.querySelector(`.cart-table__item[data-key="${currentItemPrices.key}"]`);
          currentItemComparePrice = currentItemPrices.compare_at_price;

          // Skip when compare at price is not set
          if (currentItemComparePrice == 0) {
            updateCompareAtPricesCache({
              key: currentItem.key,
              compareAtPrice: currentItemComparePrice
            });

            continue;
          }

          if (lineItem) {
            updateCompareAtPrices({
              lineItem,
              compareAtPrice: currentItemComparePrice
            });
          }
        } else {
          const productData = await fetch(`/products/${currentItem.handle}.js`).then(res => res.json());

          if (productData && productData.variants.length) {
            const currentVariant = productData.variants.find(variant => variant.id == currentItem.id);

            // Skip when compare at price is not set
            if (!currentVariant.compare_at_price || currentVariant.compare_at_price == 0) {
              updateCompareAtPricesCache({
                key: currentItem.key,
                compareAtPrice: currentVariant.compare_at_price
              });

              continue;
            }

            const lineItem = document.querySelector(`.cart-table__item[data-id="${currentVariant.id}"]`);
            currentItemComparePrice = window.theme && window.theme.moneyFormat ? formatMoney(currentVariant.compare_at_price, window.theme.moneyFormat) : currentVariant.compare_at_price;

            if (lineItem) {
              updateCompareAtPrices({
                lineItem,
                compareAtPrice: currentItemComparePrice
              });
            }
          }
        }

        updateCompareAtPricesCache({
          key: currentItem.key,
          compareAtPrice: currentItemComparePrice
        });
      }
    }
  }

  /**
   * Render cart template.
   * @param {Object} data - Cart.js data.
   */
  async function render(data) {
    console.log('RENDER!', data);
    nodeSelectors.container.innerHTML = getCartTemplate(data);
    constructQuantitySelectors();
    updateCartPrices(data);
    dataLayerLinks();

    await loadCompareAtPrices(data);

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }

    Frame.EventBus.emit('CartTable:rendered', data);
  }

  /**
  * Get cart template
  * @param {Object} data - Cart data.
  * @returns {String}
  */
  function getCartTemplate(data) {
    const lineItemsTemplate = data.items.map((item, index) => {
      if (isAddOnItem(item) || isServicesItem(item)) {
        return '';
      }

      return getLineItemTemplate(item, index);
    }).join('');

    return `
      <div class="cart-table__body ajax-cart">
        ${lineItemsTemplate}
      </div>
    `;
  }

  /**
   * Get line item template.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */
  function getLineItemTemplate(item, index) {
    return `
      <div
        class="cart-table__item ajax-cart__line-item"
        data-key="${item.key}"
        data-id="${item.id}"
        data-index="${index}"
        data-engraved="${item.properties.ENGRAVING ? true : false}"
        js-cart-table="lineItem"
      >
        <div class="cart-table__image ajax-cart__image">
          <a href="${item.url}">
            ${getLineItemImage(item)}
          </a>
        </div>

        <div class="cart-table__description">
          <div class="row no-margin no-gutter">
            <div class="col xs12">
              <div class="ajax-cart__inner">
                ${isVendorEnabled() ? getVendorTitle(item) : ''}

                <a href="${item.url}" class="ajax-cart__title" js-ajax-cart="productLink">${item.product_title}</a>
              </div>
            </div>
          </div>

          <div class="row no-margin no-gutter">
            <div class="col xs12 s6 m7">
              ${getVariantTitle(item)}

              ${getEngravingMessage(item)}

              <button
                class="ajax-cart__remove"
                type="button"
                data-cy="removeProduct"
                data-key="${item.key}"
                data-engraved="${item.properties.ENGRAVING ? true : false}"
                js-ajax-cart="remove"
              >
                ${decodeHTMLEntities(theme.strings.cart.general.remove)}
              </button>

              ${getAddOnMessage(item)}
            </div>


            <div class="col xs12 s6 m5">
              <div class="ajax-cart__amount">
                <div class="cart-table__quantity-selector" data-label="Quantity">
                  ${quantitySelectorTemplate(item, index, 'cart-table')}
                </div>

                <div class="ajax-cart__price" js-cart-table="lineItemPrice">
                  ${getLineItemPrice(item)}
                  <s data-compare-at-price="" style="display:none;"></s>
                </div>
              </div>
            </div>
          </div>
        </div>
        <script js-product-json>
          {
            "name": "${handleize(item.product_title).replace(/-/g, '_')}",
            "id": "${item.variant_id}",
            "price": ${(item.price / 100)},
            "brand": "alessi",
            "variant": "${(item.variant_options[0] && !item.product_has_only_default_variant) ? item.variant_options[0] : ``}",
            "quantity": ${item.quantity},
            "dimension16": "${(item.variant_options[1]) ? item.variant_options[1] : ``}"
          }
        </script>
      </div>
    `;
  }

  /**
   * Get line item image template.
   * @param {Object} item - Variant line item object from cart.js.
   * @returns {String}
   */
  function getLineItemImage(item) {
    if (!item.image) {
      return '';
    }

    return `
    <div class="ajax-cart__image responsive-image__wrapper">
      <div class="ajax-cart__image-container">
        <img
          alt="${item.title}"
          sizes="auto"
          src="${getSizedImageUrl(item.image, '50x')}"
          srcset="
            ${getSizedImageUrl(item.image, '120x')} 120w,
            ${getSizedImageUrl(item.image, '240x')} 240w,
            ${getSizedImageUrl(item.image, '320x')} 320w
          "
        >
      </div>
    </div>
    `;
  }

  /**
 * Check if current item is an add-on.
 * @param {Object} item - Cart variant item.
 * @returns {Boolean}
 */
  function isAddOnItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return (settings.hideAddOns && item.product_type.toLowerCase() === 'add-on');
  }

  /**
* Check if current item is a services.
* @param {Object} item - Cart variant item.
* @returns {Boolean}
*/
  function isServicesItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return (item.product_type.toLowerCase() === 'services');
  }

  /**
   * Check if vendor title setting is enabled.
   * @returns {Boolean}
   */
  function isVendorEnabled() {
    return JSON.parse(nodeSelectors.container.dataset.enableVendorTitle);
  }



  /**
   * Get vendor title template.
   * @param {Object} item - Cart item to get vendor title from.
   * @returns {String}
   */
  function getVendorTitle(item) {
    return `<p class="ajax-cart__vendor meta">${item.vendor}</p>`;
  }

  /**
   * Get variant titles.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */
  function getVariantTitle(item) {
    return (item.variant_options.length && !item.variant_options[0].includes('Default Title'))
      ? `<p class="ajax-cart__variant-title caption">${item.variant_title}</p>`
      : '';
  }

  /**
   * Get add-on message.
   * @param {Object} item - Line item data object.
   */
  function getAddOnMessage(item) {
    let largeItemMsg = '';

    if (item.properties !== null) {
      if (item.properties._large_item !== undefined) {
        largeItemMsg = `
          <p class="cart-table__large-message caption">
            <span>${theme.icons.information}</span>
            <span>${theme.strings.cart.add_on.largeItemMsg}</span>
          </p>
        `;
      }
    }

    return largeItemMsg;
  }
  /**
 * Get engraving message.
 * @param {Object} item - Line item data object.
 */
  function getEngravingMessage(item) {
    let engravingProperty = '';

    if (item.properties !== null) {
      if (item.properties.ENGRAVING !== undefined) {
        engravingProperty = `
            <p class="cart-table__engraving caption">
              <span>${theme.engraving.label} ${item.properties.ENGRAVING}</span>
            </p>
          `;
      }
    }
    return engravingProperty;
  }

  /**
   * Get formatted line item price.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */
  function getLineItemPrice(item) {
    if (item.properties && item.properties.ENGRAVING) {
      var item_discounted_price = item.discounted_price + theme.engraving.price;
      var item_price = item.price + theme.engraving.price;
      var original_price = original_price + theme.engraving.price;
    } else {
      var item_discounted_price = item.discounted_price;
      var item_price = item.price;
      var original_price = original_price;
    }
    if (item.original_line_price !== item.line_price) {
      if (item.discounted_price !== item.price) {
        return `
          <span class="visually-hidden">${item_discounted_price}</span>
          <span js-currency="price" data-price="${item_discounted_price}">
            ${formatMoney(item_discounted_price, theme.moneyFormat)}
          </span>

          <span class="visually-hidden">${item_price}</span>
          <s js-currency="price" data-price="${item_price}">
            ${formatMoney(item_price, theme.moneyFormat)}
          </s>
        `;
      }

      return `
        <span class="visually-hidden">${item_price}</span>
        <span js-currency="price" data-price="${item_price}">
          ${formatMoney(item_price, theme.moneyFormat)}
        </span>

        <span class="visually-hidden">${original_price}</span>
        <s js-currency="price" data-price="${original_price}">
          ${formatMoney(original_price, theme.moneyFormat)}
        </s>
      `;
    }

    return `
      <span js-currency="price" data-price="${item_price}">
        ${formatMoney(item_price, theme.moneyFormat)}
      </span>
    `;
  }

  /**
   * Set event delegation to remove items on container.
   * @param {HTMLElement} container - The container to apply the remove events on.
   */
  function setEventListeners(container) {
    on('click', container, (event) => handleRemoveEvent(event));
    toggleFootnote();
  }

  /**
   * Handle remove events.
   * @param {Event} event - Click event.
   */
  function handleRemoveEvent(event) {
    if (!isTargetRemove(event.target)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:removingItem', event.target.closest(selectors.lineItem));

    const data = {
      dataLayer: event.target.closest(selectors.lineItem).querySelector(selectors.productJson).innerHTML,
    };

    cart.removeItem(event.target.dataset.key)
      .then((response) => {
        Frame.EventBus.emit('AjaxCart:itemRemoved', response);
        return response;
      })
      .catch((error) => {
        window.location.reload();
        throw new Error('Could not remove item from cart', error);
      });

    window.dataLayer.push({
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: window.theme.currency,
        eventPageLocator: 'cart',
        remove: {
          actionField: {
            list: '',
          },
          products: [JSON.parse(data.dataLayer)],
        },
      },
    });
  }

  /**
   * Detect remove target click.
   * @param {HTMLElement} target - Clicked target to check remove attributes against.
   * @returns {Boolean}
   */
  function isTargetRemove(target) {
    return (
      typeof target.attributes[selectors.ajaxCart] !== 'undefined' &&
      target.attributes[selectors.ajaxCart].nodeValue === 'remove'
    );
  }

  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  function constructQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll(selectors.quantitySelector);

    [...quantitySelectors].forEach((item) => {
      QuantitySelector(`${item.id}`, () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {

          if (handlingQuantity) { return; }
          handleQuantityEvent(item);
        }, 250);
      }).setInitState();
    });
  }

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  function handleQuantityEvent(element) {
    handlingQuantity = true;
    Frame.EventBus.emit('CartTable:updating');

    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10),
    };

    updateItem(data.key, { quantity: data.quantity })
      .then(() => {
        Frame.EventBus.emit('AjaxCart:itemUpdated');
        Frame.EventBus.emit('CartTable:updated');
        handlingQuantity = false;
      })
      .catch((error) => {
        handlingQuantity = false;
        Frame.EventBus.emit('CartTable:updated');
        if (typeof response === 'undefined') {
          return;
        }
        throw new Error('Could not update item data', error);
      });
  }

  /**
   * Update subtotal and discount total.
   * @param {Object} data - Cart response data.
   */
  function updateCartPrices(data) {
    updateSubtotal(data);
    updateDiscountTotal(data.total_discount);
    updateTotal(data.total_price);
  }

  /**
  * Update subtotal.
  * @param {Object} data - Cart response data.
  */
  function updateSubtotal(data) {
    const addOnPrice = getAddOnPrice(data);

    const price = ~~data.items_subtotal_price - addOnPrice;

    [...nodeSelectors.subtotal].forEach((element) => {
      element.setAttribute('data-price', price);
      element.innerHTML = formatMoney(price, theme.moneyFormat);
    });
  }

  /**
   * Get cart add-on price.
   * @param {Object} data - Cart response data.
   */
  function getAddOnPrice(data) {
    let addOnPrice = 0;

    data.items.forEach((item) => {
      if (item.product_type === 'add-on') {
        addOnPrice += item.price;
      }
    });

    return addOnPrice;
  }



  /**
  * Update total.
  * @param {Number} price - Total cart price number.
  */
  function updateTotal(price) {
    nodeSelectors.total.setAttribute('data-price', price);
    nodeSelectors.total.innerHTML = formatMoney(price, theme.moneyFormat);
  }

  /**
   * Update discount total.
   * @param {Number} price - Total discount price number.
   */
  function updateDiscountTotal(price) {
    if (!isCartDiscounted(price)) {
      hideDiscountLabel();
      return;
    }

    if (!nodeSelectors.totalDiscountPrice) { return; }

    showDiscountLabel();
    nodeSelectors.totalDiscountPrice.setAttribute('data-price', price);
    nodeSelectors.totalDiscountPrice.innerHTML = formatMoney(price, theme.moneyFormat);
  }

  /**
   * Set loading state.
   * @param {Object} element - Line item row in DOM.
   */
  function setLoadingState(element) {
    Frame.EventBus.emit('CartTable:updating');
    element.classList.add(cssClasses.isRemoving);
  }

  /**
   * Check discount total.
   * @param {Number} total - Total discount price number.
   */
  function isCartDiscounted(total) {
    return total > 0;
  }

  /**
   * Hide discount label.
   */
  function hideDiscountLabel() {
    if (!nodeSelectors.totalDiscountContainer) { return; }
    nodeSelectors.totalDiscountContainer.classList.add(cssClasses.hidden);
  }

  /**
   * Show discount label.
   */
  function showDiscountLabel() {
    if (!nodeSelectors.totalDiscountContainer) { return; }
    nodeSelectors.totalDiscountContainer.classList.remove(cssClasses.hidden);
  }

  /**
   *  Toggle footnote content.
   */
  function toggleFootnote() {
    [...nodeSelectors.footnote].forEach((trigger) => {
      on('click', trigger, () => {
        const content = trigger.dataset.trigger;
        nodeSelectors.tooltip.classList.add(cssClasses.active);

        nodeSelectors.footnoteContent.forEach((note) => {
          const noteContent = note.dataset.content;
          note.classList.remove(cssClasses.active);

          if (content === noteContent) {
            note.classList.add(cssClasses.active);
          }
        });
      });
    });

    on('click', nodeSelectors.tooltipClose, () => {
      nodeSelectors.tooltip.classList.remove(cssClasses.active);
    });
  }

  function disableAddToCart() {
    if (nodeSelectors.termsContainer !== null) {
      nodeSelectors.submit.setAttribute('disabled', true)
      nodeSelectors.submit.classList.remove('button--fill')
    }
  }
  function enableAddToCart() {
    nodeSelectors.submit.classList.add('button--fill')
    nodeSelectors.submit.removeAttribute('disabled', true)
    nodeSelectors.termsEngraving.checked = true
  }

  function toggleAddToCart() {
    if (nodeSelectors.termsEngraving.checked) {
      enableAddToCart();
    } else {
      disableAddToCart();
    }
  }



  /**
   * Update cart.
   * - Uses `/cart/change.js` endpoint to change the quantity of a cart lineitem.
   * - Used to update item quantity on quantity selector change.
   * @param {String} key - Line item key
   * @param {Object} options - Options to pass to the change endpoint
   */
  function updateItem(key, options) {
    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }

    cancelToken = axios.CancelToken.source();

    const payload = {
      id: key,
      quantity: options.quantity,
    };

    if (options.properties) {
      payload.properties = options.properties;
    }

    return axios.post('/cart/change.js', payload, {
      cancelToken: cancelToken.token,
    });
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
